import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, onSnapshot, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { WebsiteOnboarding, OnboardingStatus } from '../lib/db/types';
import type { OnboardingFormData } from '../lib/validations/onboarding';

export interface OnboardingData {
  data: OnboardingFormData;
  status: OnboardingStatus;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface UseCustomerOnboardingReturn {
  onboarding: OnboardingData | null;
  loading: boolean;
  error: Error | null;
  saveOnboarding: (data: OnboardingFormData, status?: OnboardingStatus) => Promise<void>;
}

export function useCustomerOnboarding(customerId: string): UseCustomerOnboardingReturn {
  const [onboarding, setOnboarding] = useState<OnboardingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!customerId) {
      setOnboarding(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const onboardingRef = doc(db, 'customers', customerId, 'website', 'onboarding');
    const unsubscribe = onSnapshot(
      onboardingRef,
      (snapshot) => {
        setLoading(false);
        if (!snapshot.exists()) {
          setOnboarding(null);
          return;
        }

        const data = snapshot.data() as WebsiteOnboarding;
        setOnboarding({
          data: data as unknown as OnboardingFormData,
          status: data.status || 'not_started',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        });
      },
      (snapshotError) => {
        console.error('Error loading onboarding:', snapshotError);
        setError(snapshotError instanceof Error ? snapshotError : new Error(String(snapshotError)));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [customerId]);

  const saveOnboarding = useCallback(
    async (data: OnboardingFormData, status: OnboardingStatus = 'in_progress') => {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      const onboardingRef = doc(db, 'customers', customerId, 'website', 'onboarding');
      const now = Timestamp.now();
      const existing = await getDoc(onboardingRef);

      const nextData: Partial<WebsiteOnboarding> = {
        ...data,
        tenantId: customerId,
        status,
        updatedAt: now,
      };

      if (!existing.exists()) {
        nextData.createdAt = now;
      }

      await setDoc(onboardingRef, nextData, { merge: true });
    },
    [customerId]
  );

  return {
    onboarding,
    loading,
    error,
    saveOnboarding,
  };
}

export default useCustomerOnboarding;
